import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styles from './index.module.scss';
import {
  Button,
  Select,
  Space,
  Form,
  Table,
  DatePicker,
  Segmented,
  message,
  Modal,
  Input,
  InputNumber,
  Tooltip,
} from 'antd';
import SignatureCanvas from 'react-signature-canvas';
import { Trans } from 'react-i18next';
import LOCALS from 'commons/locals';
import {
  StatusListDefault,
  OrderType,
  OMS_RECYCLE_ORDER_STATUS_MAP,
  ShopMaping,
  getRecycleOrderStatusText,
} from 'constants/RecyclingConsignment';
import {
  fetchOmsRecyclingList,
  fetchStatusTotal,
  omsOrderRecycleOrderUpdateInfo,
  sendOmsRecycleOrderFundsTransferNotificationBatch,
} from 'apis/oms';
import { OmsRecycleOrderDetail, OmsRecycleOrderStatusTotal } from 'types/oms';
import usePagination from 'commons/hooks/usePagination';
import { useNavigate } from 'react-router-dom';
import useMoreModal from 'pages/rrs/recycling-consignment/modal-components';
import BatchPaymentVoucherModal from './modal-components/batch-payment-voucher-modal';
import { getColumns } from './utils';
import ProductIdSearch, {
  getNextAppliedProductId,
  parseProductIdSearchValue,
} from './components/product-id-search';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import findCascaderOptionById from 'utils/findCascaderOptionById';
import useResource from 'commons/hooks/useResource';
import { OMS_RECYCLE_ORDER_PRIORITY_OPTION_LIST } from 'commons/options';
import i18n from '../../../i18n';
import classNames from 'classnames';
import { openSignedRecycleOrderContract } from 'utils/openSignedRecycleOrderContract';
import { selectUserInfo } from 'store/slices/userInfoSlice';
import {
  useQueryState,
  parseAsInteger,
  parseAsString,
  parseAsArrayOf,
} from 'nuqs';
import dayjs from 'dayjs';
import { getShopConfig, SHOP_MAP } from 'constant/shop';
import exportToExcel from 'utils/exportToExcel';
import { thousands } from 'utils/tools';

const { RangePicker } = DatePicker;

const EXPORT_MAX_COUNT = 400;

const PRIORITY_TEXT: Record<number, string> = {
  1: 'Low',
  2: 'Medium',
  3: 'High',
};

const formatExportTime = (time?: string) =>
  time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-';

const formatQuotedAmount = (record: OmsRecycleOrderDetail) => {
  const item = record.omsRecycleOrderItem;
  const order = record.omsRecycleOrder;
  if (!item || !order) return '-';

  const currency = order.currency || '';
  const {
    finalRecyclePrice = '',
    finalSalePrice = '',
    maxRecyclePrice = '',
    maxSalePrice = '',
    minRecyclePrice = '',
    minSalePrice = '',
  } = item;

  const hasPrice = (value?: number | string) =>
    value !== '' && value !== undefined && value !== null;

  const withCurrency = (value?: number | string) => {
    if (!hasPrice(value)) return '-';
    return `${currency} ${thousands(value)}`.trim();
  };

  const rangeText = (min?: number | string, max?: number | string) => {
    const hasMin = hasPrice(min);
    const hasMax = hasPrice(max);
    if (!hasMin && !hasMax) return '-';
    if (hasMin && hasMax) {
      return `${currency} ${thousands(min)}~${thousands(max)}`.trim();
    }
    return `${currency} ${thousands(hasMin ? min : max)}`.trim();
  };

  if (order.status === 9) {
    if (order.type === 2) {
      return `${i18n.t(LOCALS.recycling_confirmation_quote)}: ${withCurrency(
        finalRecyclePrice,
      )}`;
    }
    if (order.type === 1) {
      return `${i18n.t(LOCALS.consignment_confirmation_quote)}: ${withCurrency(
        finalSalePrice,
      )}`;
    }
  }

  if (finalRecyclePrice || finalSalePrice) {
    return [
      `${i18n.t(LOCALS.recycling_confirmation_quote)}: ${withCurrency(
        finalRecyclePrice,
      )}`,
      `${i18n.t(LOCALS.consignment_confirmation_quote)}: ${withCurrency(
        finalSalePrice,
      )}`,
    ].join('\n');
  }

  return [
    `${i18n.t(LOCALS.instant_sale)}: ${rangeText(
      minRecyclePrice,
      maxRecyclePrice,
    )}`,
    `${i18n.t(LOCALS.consignment_preliminary_valuation)}: ${rangeText(
      minSalePrice,
      maxSalePrice,
    )}`,
  ].join('\n');
};

const getLatestShopRemark = (record: OmsRecycleOrderDetail) => {
  return (
    record.omsRecycleOrderLogList
      ?.filter(
        (log) => log.shopRemark && log.shopRemark.trim().startsWith('备注: '),
      )
      ?.sort(
        (a, b) =>
          new Date(b.createTime).getTime() - new Date(a.createTime).getTime(),
      )?.[0]?.shopRemark || '-'
  );
};

const RecyclingConsignmentList = ({
  initialType,
}: {
  initialType?: number;
}) => {
  const viewUserInfoResource = useResource(
    'recycling-consignment-view-user-info',
  );

  const navigate = useNavigate();
  /** 选择key */
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  /** table */
  const { loading, setLoading, total, setTotal, dataSource, setDataSource } =
    usePagination<OmsRecycleOrderDetail>();
  /** 操作行 */
  const optionRowDataRef = useRef<OmsRecycleOrderDetail>({});
  /** Modal参数 */
  const modalOptionRef = useRef<any>({});
  /** 状态总数 */
  const [statusObject, setStatusObject] =
    useState<OmsRecycleOrderStatusTotal>();

  /** 操作备注相关状态 */
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [remarkForm] = Form.useForm();
  const [batchPaymentVoucherModalOpen, setBatchPaymentVoucherModalOpen] =
    useState(false);
  const [batchSendFundsTransferLoading, setBatchSendFundsTransferLoading] =
    useState(false);
  const [signedContractModalOpen, setSignedContractModalOpen] = useState(false);
  const [signedContractSubmitting, setSignedContractSubmitting] =
    useState(false);
  const [exporting, setExporting] = useState(false);
  const signatureRef = useRef<SignatureCanvas | null>(null);

  // page query
  const [pageNum, setPageNum] = useQueryState(
    'pageNum',
    parseAsInteger.withDefault(1),
  );
  const [pageSize, setPageSize] = useQueryState(
    'pageSize',
    parseAsInteger.withDefault(10),
  );
  const [status, setStatus] = useQueryState(
    'status',
    parseAsString.withDefault(initialType ? 'all' : '1'),
  );
  const [submissionId, setSubmissionId] = useQueryState(
    'submissionId',
    parseAsInteger,
  );
  const [productId, setProductId] = useQueryState(
    'productId',
    parseAsString.withDefault(''),
  );
  const [memberId, setMemberId] = useQueryState('memberId', parseAsInteger);
  const [code, setCode] = useQueryState('code', parseAsString.withDefault(''));
  const [email, setEmail] = useQueryState(
    'email',
    parseAsString.withDefault(''),
  );
  const [phone, setPhone] = useQueryState(
    'phone',
    parseAsString.withDefault(''),
  );
  const [priority, setPriority] = useQueryState('priority', parseAsInteger);
  const [type, setType] = useQueryState(
    'type',
    initialType ? parseAsInteger.withDefault(initialType) : parseAsInteger,
  );
  const [storeIds, setStoreIds] = useQueryState(
    'storeIds',
    parseAsArrayOf(parseAsInteger),
  );
  const [isTradeIn, setIsTradeIn] = useQueryState('isTradeIn', parseAsInteger);
  const [range, setRange] = useQueryState(
    'range',
    parseAsArrayOf(parseAsString).withDefault([]),
  );

  const {
    materialCascaderOptionsMap,
    productCategoryCascaderOptions,
    colorSelectOptions,
    rankSelectOptions,
    stampSelectOptions,
    hardwareSelectOptions,
  } = useAppSelector(selectGlobalInfo);

  const { shop } = useAppSelector(selectUserInfo);
  const [localProductId, setLocalProductId] = useState(productId);
  const productIdList = useMemo(
    () => parseProductIdSearchValue(productId),
    [productId],
  );

  useEffect(() => {
    setLocalProductId(productId);
  }, [productId]);

  const commitPendingProductId = useCallback(() => {
    const nextProductId = getNextAppliedProductId({
      appliedValue: productId,
      draftValue: localProductId,
    });

    if (nextProductId !== productId) {
      setProductId(nextProductId);
    }
  }, [localProductId, productId, setProductId]);

  /** 状态总数 */
  useEffect(() => {
    fetchStatusTotal().then((d) => {
      const { data } = d;
      let count: number = 0;
      Object.values(data).forEach((d) => (count += d));
      setStatusObject({ ...data, count });
    });
  }, []);

  const getListPayload = useCallback(
    (nextPageNum: number, nextPageSize: number) => ({
      pageNum: nextPageNum,
      pageSize: nextPageSize,
      status: status === 'all' ? '' : status,
      submissionId: submissionId || undefined,
      productId: productIdList.length === 1 ? productIdList[0] : undefined,
      productIdList: productIdList.length ? productIdList : undefined,
      memberId: memberId || undefined,
      code: code || undefined,
      email: email || undefined,
      phone: phone || undefined,
      priority: priority || undefined,
      type: type || undefined,
      storeIds:
        storeIds === null
          ? shop
            ? [shop]
            : undefined
          : storeIds.length > 0
            ? storeIds
            : undefined,
      isTradeIn: isTradeIn || undefined,
      beginCommitTime: range[0] || undefined,
      endCommitTime: range[1] || undefined,
    }),
    [
      status,
      submissionId,
      productIdList,
      memberId,
      code,
      email,
      phone,
      priority,
      type,
      storeIds,
      shop,
      isTradeIn,
      range,
    ],
  );

  /** 加载接口 */
  const getList = useCallback(() => {
    setLoading(true);
    fetchOmsRecyclingList(getListPayload(pageNum, pageSize))
      .then((data) => {
        setTotal(data.data.total || 0);
        setDataSource(data.data.list);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [getListPayload, pageNum, pageSize, setLoading, setTotal, setDataSource]);

  useEffect(() => {
    getList();
  }, [getList]);

  const selectedOrders = useMemo(() => {
    const idSet = new Set(selectedRowKeys);
    return dataSource.filter((row) => {
      const id = row?.omsRecycleOrder?.id;
      if (!id) return false;
      return idSet.has(id);
    });
  }, [dataSource, selectedRowKeys]);

  const canBatchSendFundsTransferNotification = useMemo(() => {
    if (!selectedOrders.length) return false;
    const allowedStatuses = new Set<number>([
      OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_PAYMENT,
      OMS_RECYCLE_ORDER_STATUS_MAP.WAIT_PAYMENT_RECEIVED,
    ]);
    return selectedOrders.every((o) => {
      const s = o?.omsRecycleOrder?.status;
      return typeof s === 'number' && allowedStatuses.has(s);
    });
  }, [selectedOrders]);

  const handleBatchSendFundsTransferNotification = useCallback(() => {
    if (selectedRowKeys.length === 0) {
      message.warning(i18n.t(LOCALS.please_select_orders) as string);
      return;
    }
    if (!canBatchSendFundsTransferNotification) {
      message.warning(
        '仅支持对【待打款/待确认收款】状态的订单批量发送打款通知',
      );
      return;
    }

    const ids = selectedRowKeys.map((k) => String(k)).filter(Boolean);

    const resolveStoreLabel = (storeId?: number) => {
      if (!storeId) return '-';
      return getShopConfig(storeId).label;
    };

    const addAmount = (
      map: Map<string, number>,
      currencyRaw: string,
      v: any,
    ) => {
      const currency = (currencyRaw?.trim() || '').toUpperCase() || '-';
      const amount = Number(v ?? 0) || 0;
      map.set(currency, (map.get(currency) ?? 0) + amount);
    };

    const formatAmountMap = (map: Map<string, number>) => {
      if (!map.size) return '-';
      return Array.from(map.entries())
        .map(
          ([currency, amount]) =>
            `${amount.toLocaleString('ja-JP')} ${currency}`,
        )
        .join(', ');
    };

    // 预览：按店铺分组、按币种合计
    const byStore = new Map<
      number | undefined,
      { count: number; amount: Map<string, number> }
    >();
    const totalAmount = new Map<string, number>();
    const orderLineItems = selectedOrders.map((o) => {
      const orderId = o?.omsRecycleOrder?.id;
      const storeId = o?.omsRecycleOrder?.storeId;
      const type = o?.omsRecycleOrder?.type;
      const currency = o?.omsRecycleOrder?.currency || '';
      const amountValue =
        type === 1
          ? o?.omsRecycleOrderItem?.finalSalePrice
          : o?.omsRecycleOrderItem?.finalRecyclePrice;

      const typeLabel = type === 1 ? '委託' : type === 2 ? '買取' : '-';
      const amountNum =
        amountValue !== undefined && amountValue !== null
          ? Number(amountValue)
          : NaN;
      const currencyText = (currency?.trim() || '').toUpperCase();
      const priceText =
        Number.isFinite(amountNum) && amountNum !== 0
          ? `${amountNum.toLocaleString('ja-JP')} ${currencyText || '-'}`
          : currencyText
            ? `- ${currencyText}`
            : '-';

      return {
        key: orderId ? String(orderId) : `${storeId}-${typeLabel}-${priceText}`,
        storeId,
        typeLabel,
        priceText,
      };
    });
    selectedOrders.forEach((o) => {
      const storeId = o?.omsRecycleOrder?.storeId;
      const currency = o?.omsRecycleOrder?.currency || '';
      const orderType = o?.omsRecycleOrder?.type;
      const amountValue =
        orderType === 1
          ? o?.omsRecycleOrderItem?.finalSalePrice
          : o?.omsRecycleOrderItem?.finalRecyclePrice;

      const entry = byStore.get(storeId) ?? {
        count: 0,
        amount: new Map<string, number>(),
      };
      entry.count += 1;
      addAmount(entry.amount, currency, amountValue);
      byStore.set(storeId, entry);

      addAmount(totalAmount, currency, amountValue);
    });

    // 使用 state 来管理备注
    let remarkValue = '';

    Modal.confirm({
      title: '批量发送打款通知',
      width: 700,
      content: (
        <div className="space-y-2 mb-4">
          <div>
            将对选中的 <b>{ids.length}</b>{' '}
            单发送钉钉打款提醒（会按店铺自动拆分合并消息）。
          </div>
          <div>
            <div className="text-gray-500">选中订单明细：</div>
            <div className="mt-1">
              <Table
                size="small"
                bordered
                pagination={false}
                dataSource={orderLineItems}
                rowKey={(r) => r.key}
                scroll={{ y: 220 }}
                columns={[
                  {
                    title: '店铺',
                    dataIndex: 'storeId',
                    key: 'storeId',
                    ellipsis: true,
                    render: (storeId) => resolveStoreLabel(storeId),
                  },
                  {
                    title: '类型',
                    dataIndex: 'typeLabel',
                    key: 'typeLabel',
                    width: 90,
                    ellipsis: true,
                  },
                  {
                    title: '价格',
                    dataIndex: 'priceText',
                    key: 'priceText',
                    align: 'right',
                    width: 160,
                    ellipsis: true,
                  },
                ]}
              />
            </div>
          </div>
          <div>
            <div className="text-gray-500">分店铺预览：</div>
            <div className="grid gap-1">
              {Array.from(byStore.entries()).map(([storeId, info]) => (
                <div
                  key={String(storeId)}
                  className="flex justify-between gap-4"
                >
                  <div className="font-medium">
                    {resolveStoreLabel(storeId)}（{info.count}）
                  </div>
                  <div className="text-right">
                    {formatAmountMap(info.amount)}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-gray-500 mb-2 font-medium">
              {i18n.t(LOCALS.remark)}：
            </div>
            <Input.TextArea
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              autoSize={{ minRows: 4, maxRows: 6 }}
              maxLength={200}
              showCount
              defaultValue=""
              onChange={(e) => {
                remarkValue = e.target.value;
              }}
            />
          </div>
        </div>
      ),
      okText: i18n.t(LOCALS.confirm) as string,
      cancelText: i18n.t(LOCALS.cancel) as string,
      onOk: async () => {
        try {
          setBatchSendFundsTransferLoading(true);
          await sendOmsRecycleOrderFundsTransferNotificationBatch({
            omsRecycleOrderIdList: ids,
            remark: remarkValue || undefined,
          });
          message.success(i18n.t(LOCALS.successful_operation));
          getList();
          setSelectedRowKeys([]);
        } finally {
          setBatchSendFundsTransferLoading(false);
        }
      },
    });
  }, [
    canBatchSendFundsTransferNotification,
    getList,
    selectedOrders,
    selectedRowKeys,
  ]);

  const getSelectedRows = useCallback(() => {
    return dataSource.filter((row) => {
      const id = row?.omsRecycleOrder?.id;
      if (!id) return false;
      return selectedRowKeys.includes(id);
    });
  }, [dataSource, selectedRowKeys]);

  const validateSelectedRowsForBatchPrint = useCallback(
    (selectedRows: OmsRecycleOrderDetail[]) => {
      if (selectedRows.length === 0) {
        message.warning(i18n.t('Please select at least one order'));
        return false;
      }

      const sameType = selectedRows.every(
        (d) =>
          d.omsRecycleOrder?.type === selectedRows[0].omsRecycleOrder?.type,
      );
      if (!sameType) {
        message.warning(i18n.t('consignment_orders_and'));
        return false;
      }

      if (selectedRows.length > 1) {
        const submissionId = selectedRows[0].omsRecycleOrder?.submissionId;
        if (!submissionId) {
          message.warning('所选订单没有登记ID，无法批量打印');
          return false;
        }

        const sameSubmissionId = selectedRows.every(
          (d) => d.omsRecycleOrder?.submissionId === submissionId,
        );
        if (!sameSubmissionId) {
          message.warning('请选择同一登记ID下的订单');
          return false;
        }
      }

      return true;
    },
    [],
  );

  const handleOpenSignedContractModal = useCallback(() => {
    const selectedRows = getSelectedRows();
    if (!validateSelectedRowsForBatchPrint(selectedRows)) return;
    signatureRef.current?.clear();
    setSignedContractModalOpen(true);
  }, [getSelectedRows, validateSelectedRowsForBatchPrint]);

  const handleSignedContractSubmit = useCallback(async () => {
    const signature = signatureRef.current;
    if (!signature || signature.isEmpty()) {
      message.warning(
        String(i18n.t(LOCALS.signed_contract_signature_required)),
      );
      return;
    }

    const selectedRows = getSelectedRows();
    if (!validateSelectedRowsForBatchPrint(selectedRows)) return;

    setSignedContractSubmitting(true);
    try {
      const selectedOrderIds = selectedRows
        .map((row) => row.omsRecycleOrder?.id)
        .filter(Boolean) as string[];

      openSignedRecycleOrderContract({
        orderIdList: selectedOrderIds,
        signatureImage: signature.getTrimmedCanvas().toDataURL('image/png'),
      });
    } catch (error) {
      console.error('Error generating signed contract PDF:', error);
      message.error(String(i18n.t(LOCALS.signed_contract_generate_pdf_failed)));
    } finally {
      setSignedContractSubmitting(false);
    }
  }, [getSelectedRows, validateSelectedRowsForBatchPrint]);

  /** 操作备注提交逻辑 */
  const onRemarkOk = useCallback(async () => {
    const data = await remarkForm.validateFields();
    const orderId = optionRowDataRef.current?.omsRecycleOrder?.id;

    if (!orderId) {
      message.error('订单信息错误');
      return;
    }

    Modal.confirm({
      title: <Trans i18nKey={LOCALS.confirm_submit} />,
      onOk: async () => {
        await omsOrderRecycleOrderUpdateInfo({
          omsRecycleOrderId: orderId,
          note: data.note,
        });

        message.success(i18n.t(LOCALS.successful_operation));
        setRemarkModalOpen(false);
        remarkForm.resetFields();
        getList();
      },
    });
  }, [getList, remarkForm]);

  /** 弹窗相关数据 */
  const {
    valuationModalElement,
    setValuationModalOpen,
    shiplableModalElement,
    setShiplableModalOpen,
    goodsReceivedModalElement,
    setGoodsReceivedModalOpen,
    finalValuationModalElement,
    setFinalValuationModalOpen,
    logisticsDocumentModalElement,
    setLogisticsDocumentModalOpen,
    updateFinalValuationModalElement,
    setUpdateFinalValuationModalOpen,
    confirmSettlementModalElement,
    setConfirmSettlementModalOpen,
    confirmReturnModalElement,
    setConfirmReturnModalOpen,
    createPaymentVoucherModalElement,
    setPaymentVoucherModalOpen,
    deliverGoodsModalElement,
    setDeliverGoodsModalOpen,
    setCustomerAgreeValuationModalOpen,
    customerAgreeValuationModalElement,
    setPayInfoModalOpen,
    payInfoModalElement,
  } = useMoreModal({
    dataSource: optionRowDataRef.current,
    modalOption: modalOptionRef.current,
    getLoad: getList,
  });

  const rowSelection = useMemo(() => {
    return {
      selectedRowKeys,
      onChange: (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
      },
    };
  }, [selectedRowKeys]);

  const getProductInfoLines = useCallback(
    (data: OmsRecycleOrderDetail): string[] => {
      const { omsRecycleOrderProduct } = data;
      const {
        productCategoryId = 0,
        attrColor,
        attrStamp,
        attrMaterial,
        attrHardware,
        rank,
        name,
      } = omsRecycleOrderProduct || {};
      const color: string[] = [];
      attrColor &&
        attrColor.split(',')?.forEach((d: string) => {
          const t = colorSelectOptions.find((dd) => dd.value === d);
          if (t) color.push(t.label);
        });
      const material: string[] = [];
      const target = findCascaderOptionById(
        productCategoryId,
        productCategoryCascaderOptions,
      );
      const productCategoryIds = target?.treeIds?.split(',')?.map(Number) || [];
      const mateList = materialCascaderOptionsMap[productCategoryIds[0]] || [];
      attrMaterial &&
        attrMaterial.split(',')?.forEach((d: string) => {
          const t = findCascaderOptionById(d, mateList);
          if (t) material.push(t.label);
        });
      const hardware: string[] = [];
      attrHardware &&
        attrHardware.split(',')?.forEach((d: string) => {
          const t = hardwareSelectOptions.find((dd) => dd.value === d);
          if (t) hardware.push(t.label);
        });
      const rankLabel = rankSelectOptions.find((d) => d.value === rank)?.label;

      return [
        name || '',
        rankLabel || '',
        material.join('、') || '',
        color.join('、') || '',
        stampSelectOptions.find((d) => d.value === attrStamp)?.label,
        hardware.join('、') || '',
      ].filter((item): item is string => !!item);
    },
    [
      materialCascaderOptionsMap,
      productCategoryCascaderOptions,
      colorSelectOptions,
      rankSelectOptions,
      stampSelectOptions,
      hardwareSelectOptions,
    ],
  );

  /** 渲染商品信息 */
  const ProductShowData = useCallback(
    (data: OmsRecycleOrderDetail): React.ReactNode => {
      return (
        <div className={classNames('whitespace-pre-line')}>
          {getProductInfoLines(data).join('\n')}
        </div>
      );
    },
    [getProductInfoLines],
  );

  const handleExport = useCallback(async () => {
    if (total === 0) {
      message.warning('暂无数据可导出');
      return;
    }
    if (total > EXPORT_MAX_COUNT) {
      message.warning(
        `当前筛选结果共 ${total} 条，超过 ${EXPORT_MAX_COUNT} 条无法导出，请缩小筛选范围`,
      );
      return;
    }

    setExporting(true);
    try {
      const { data } = await fetchOmsRecyclingList(
        getListPayload(1, EXPORT_MAX_COUNT),
      );
      const list = data.list || [];
      if (list.length > EXPORT_MAX_COUNT) {
        message.warning(
          `当前筛选结果超过 ${EXPORT_MAX_COUNT} 条，无法导出，请缩小筛选范围`,
        );
        return;
      }

      const headers = [
        i18n.t(LOCALS.sHXnOEVSrC),
        i18n.t(LOCALS.code),
        i18n.t(LOCALS.order_type),
        i18n.t(LOCALS.priority),
        ...(viewUserInfoResource
          ? [
              i18n.t(LOCALS.member_id),
              i18n.t(LOCALS.customer_name),
              i18n.t(LOCALS.email),
              i18n.t(LOCALS.phone),
            ]
          : []),
        i18n.t(LOCALS.shop_id),
        i18n.t(LOCALS.associated_product_id),
        i18n.t(LOCALS.product_info),
        i18n.t(LOCALS.order_status),
        i18n.t(LOCALS.quoted_amount),
        i18n.t(LOCALS.created_time),
        i18n.t(LOCALS.contract_establishment_time),
        i18n.t(LOCALS.receiving_time),
        i18n.t(LOCALS.payment_time),
        i18n.t(LOCALS.operation_remark),
      ];

      const rows = list.map((record) => {
        const order = record.omsRecycleOrder;
        const item = record.omsRecycleOrderItem;
        const member = record.umsMember;
        const typeLabel =
          OrderType.find((d) => order?.type === d.value)?.label || '-';
        const memberName =
          member?.lastName && member?.firstName
            ? `${member.lastName} ${member.firstName}`
            : '-';

        return [
          order?.submissionId || '-',
          order?.code || '-',
          typeLabel,
          order?.priority ? PRIORITY_TEXT[order.priority] || '-' : '-',
          ...(viewUserInfoResource
            ? [
                item?.memberId || order?.memberId || '-',
                memberName,
                order?.email || '-',
                order?.phone ? `+${order.phone}` : '-',
              ]
            : []),
          ShopMaping[`${order?.storeId}`] || '-',
          item?.productId || '-',
          getProductInfoLines(record).join('\n') || '-',
          getRecycleOrderStatusText(order),
          formatQuotedAmount(record),
          formatExportTime(order?.createTime),
          formatExportTime(order?.contractCreateTime),
          formatExportTime(order?.goodsReceivedTime),
          formatExportTime(order?.financialPaymentTime),
          getLatestShopRemark(record),
        ];
      });

      exportToExcel(
        [
          {
            sheetName: '回收寄卖订单',
            data: [headers, ...rows],
          },
        ],
        `回收寄卖订单-${dayjs().format('YYYY-MM-DD')}.xlsx`,
      );
    } catch {
      message.error('导出失败');
    } finally {
      setExporting(false);
    }
  }, [getListPayload, getProductInfoLines, total, viewUserInfoResource]);

  /** 表格列 */
  const columns = useCallback(
    () =>
      getColumns({
        setRowData: (data: OmsRecycleOrderDetail) =>
          (optionRowDataRef.current = data),
        setModalOption: (data: any) => (modalOptionRef.current = data),
        navigate,
        setValuationModalOpen,
        setShiplableModalOpen,
        setGoodsReceivedModalOpen,
        setLogisticsDocumentModalOpen,
        setFinalValuationModalOpen,
        setUpdateFinalValuationModalOpen,
        setConfirmSettlementModalOpen,
        setConfirmReturnModalOpen,
        setPaymentVoucherModalOpen,
        setDeliverGoodsModalOpen,
        setCustomerAgreeValuationModalOpen,
        setPayInfoModalOpen,
        setRemarkModalOpen,
        ProductShowData,
        getLoad: getList,
        status,
        viewUserInfoResource,
      }),
    [
      navigate,
      setValuationModalOpen,
      setShiplableModalOpen,
      setGoodsReceivedModalOpen,
      setLogisticsDocumentModalOpen,
      setFinalValuationModalOpen,
      setUpdateFinalValuationModalOpen,
      setConfirmSettlementModalOpen,
      setConfirmReturnModalOpen,
      setPaymentVoucherModalOpen,
      setDeliverGoodsModalOpen,
      setCustomerAgreeValuationModalOpen,
      setPayInfoModalOpen,
      ProductShowData,
      getList,
      status,
      viewUserInfoResource,
    ],
  );

  return (
    <div className={styles.RecyclingConsignmentList}>
      <div className={styles.form}>
        <div className="flex items-center gap-x-4 flex-wrap">
          <Form.Item label={i18n.t(LOCALS.sHXnOEVSrC)}>
            <InputNumber
              controls={false}
              value={submissionId}
              onChange={(e) => {
                commitPendingProductId();
                setSubmissionId(e);
                setStatus('all');
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.code)}>
            <Input
              value={code}
              onChange={(e) => {
                commitPendingProductId();
                setCode(e.target.value);
                setStatus('all');
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
            />
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.member_id)}>
            <InputNumber
              controls={false}
              value={memberId}
              onChange={(e) => {
                commitPendingProductId();
                setMemberId(e);
                setStatus('all');
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.email)}>
            <Input
              value={email}
              onChange={(e) => {
                commitPendingProductId();
                setEmail(e.target.value);
                setStatus('all');
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              style={{ width: 220 }}
            />
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.phone)}>
            <Input
              value={phone}
              onChange={(e) => {
                commitPendingProductId();
                setPhone(e.target.value);
                setStatus('all');
                setPageNum(1);
              }}
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              style={{ width: 220 }}
            />
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.consignment_product_id)}>
            <ProductIdSearch
              value={localProductId}
              onChange={setLocalProductId}
              style={{ width: 240 }}
            />
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.created_time)}>
            <RangePicker
              value={
                range.length === 2 ? [dayjs(range[0]), dayjs(range[1])] : null
              }
              onChange={(dates) => {
                commitPendingProductId();
                if (dates) {
                  setRange([
                    dayjs(dates[0]).startOf('days').format() || '',
                    dayjs(dates[1]).endOf('days').format() || '',
                  ]);
                } else {
                  setRange(null);
                }

                setStatus('all');
                setPageNum(1);
              }}
              style={{ width: 260 }}
            />
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.order_type)}>
            <Select
              disabled={!!initialType}
              value={type}
              onChange={(e) => {
                commitPendingProductId();
                setType(e);
                setStatus('all');
                setPageNum(1);
              }}
              options={OrderType}
              style={{ width: 150 }}
              placeholder={i18n.t(LOCALS.please_select)}
            ></Select>
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.priority)}>
            <Select
              placeholder={i18n.t(LOCALS.please_select)}
              value={priority}
              onChange={(e) => {
                commitPendingProductId();
                setPriority(e);
                setStatus('all');
                setPageNum(1);
              }}
              options={OMS_RECYCLE_ORDER_PRIORITY_OPTION_LIST}
            ></Select>
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.is_trade_in)}>
            <Select
              placeholder={i18n.t(LOCALS.please_select)}
              value={isTradeIn}
              onChange={(e) => {
                commitPendingProductId();
                setIsTradeIn(e);
                setStatus('all');
                setPageNum(1);
              }}
              allowClear
              options={[
                { label: i18n.t(LOCALS.no), value: 0 },
                { label: i18n.t(LOCALS.yes), value: 1 },
              ]}
              style={{ width: 150 }}
            />
          </Form.Item>
          <Form.Item label={i18n.t(LOCALS.shop)}>
            <Select
              mode="multiple"
              allowClear
              placeholder={i18n.t(LOCALS.please_select)}
              value={storeIds === null ? (shop ? [shop] : undefined) : storeIds}
              onChange={(values: number[]) => {
                commitPendingProductId();
                setStoreIds(values.length > 0 ? values : []);
                setStatus('all');
                setPageNum(1);
              }}
              options={[
                {
                  value: SHOP_MAP.GINZA,
                  label: <Trans i18nKey={LOCALS.ginza_shop} />,
                },
                {
                  value: SHOP_MAP.HONGKONG,
                  label: <Trans i18nKey={LOCALS.hongkong_shop} />,
                },
                {
                  value: SHOP_MAP.SINGAPORE,
                  label: <Trans i18nKey={LOCALS.singapore_shop} />,
                },
                {
                  value: SHOP_MAP.SINGAPORE_ASU_GST,
                  label: <Trans i18nKey={LOCALS.singapore_shop_asu_gst} />,
                },
                {
                  value: SHOP_MAP.SINGAPORE_GX,
                  label: <Trans i18nKey={LOCALS.kkIwEnEVml} />,
                },
                {
                  value: SHOP_MAP.SHANG_HAI,
                  label: <Trans i18nKey={LOCALS.shanghai_store} />,
                },
                {
                  value: SHOP_MAP.UMEDA,
                  label: <Trans i18nKey={LOCALS.umeda_store} />,
                },
                {
                  value: SHOP_MAP.DUBAI,
                  label: <Trans i18nKey={LOCALS.dubai_store} />,
                },
                {
                  value: SHOP_MAP.FRANCE,
                  label: <Trans i18nKey={LOCALS.france_store} />,
                },
                {
                  value: SHOP_MAP.NEW_YORK,
                  label: <Trans i18nKey={LOCALS.new_york_store} />,
                },
              ]}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                onClick={() => {
                  commitPendingProductId();
                  setProductId(localProductId);
                  setStatus('all');
                  setPageNum(1);
                }}
              >
                <Trans i18nKey={LOCALS.search} />
              </Button>
              <Button
                htmlType="button"
                onClick={() => {
                  setStatus('1');
                  setSubmissionId(null);
                  setMemberId(null);
                  setEmail('');
                  setPhone('');
                  setPriority(null);
                  setCode(null);
                  setLocalProductId('');
                  setProductId('');
                  setType(null);
                  setStoreIds([]);
                  setIsTradeIn(null);
                  setRange(null);
                  setPageNum(1);
                  setPageSize(10);
                  setSelectedRowKeys([]);
                }}
              >
                <Trans i18nKey={LOCALS.reset} />
              </Button>
              <Button
                type="primary"
                disabled={selectedRowKeys.length === 0}
                onClick={() => {
                  if (!selectedRowKeys.length) return;
                  window.open(
                    `/prints/oms-recycle-order-contract?orderIdList=${selectedRowKeys.join(',')}`,
                  );
                }}
              >
                {i18n.t('yYqvdvcMFh')}
              </Button>
              <Button
                disabled={selectedRowKeys.length === 0}
                onClick={handleOpenSignedContractModal}
              >
                {i18n.t(LOCALS.signed_contract_generate_pdf)}
              </Button>
              <Button
                type="primary"
                disabled={selectedRowKeys.length === 0}
                onClick={() => setBatchPaymentVoucherModalOpen(true)}
              >
                {i18n.t('batch_payment_voucher_entry')}
              </Button>
              <Button
                type="primary"
                disabled={
                  selectedRowKeys.length === 0 ||
                  !canBatchSendFundsTransferNotification
                }
                loading={batchSendFundsTransferLoading}
                onClick={handleBatchSendFundsTransferNotification}
              >
                批量发送打款通知
              </Button>
              <Tooltip
                title={
                  total > EXPORT_MAX_COUNT
                    ? `当前筛选结果共 ${total} 条，超过 ${EXPORT_MAX_COUNT} 条无法导出，请缩小筛选范围`
                    : total === 0
                      ? '暂无数据可导出'
                      : undefined
                }
              >
                <span>
                  <Button
                    disabled={total === 0 || total > EXPORT_MAX_COUNT}
                    loading={exporting}
                    onClick={handleExport}
                  >
                    {i18n.t(LOCALS.download_excel)}
                    {total > 0 ? `（${total}）` : ''}
                  </Button>
                </span>
              </Tooltip>
            </Space>
          </Form.Item>
        </div>
      </div>
      <div className={styles.tabs}>
        <Segmented
          block
          options={StatusListDefault.map((d, i) => ({
            label: (
              <div className="leading-5">
                <div>{d.label}</div>
                <div>
                  (
                  {statusObject && statusObject[d.filed]
                    ? statusObject[d.filed]
                    : 0}
                  )
                </div>
              </div>
            ),
            value: d.key,
          }))}
          value={status}
          onChange={(e) => {
            commitPendingProductId();
            setStatus(e.toString());
            setPageNum(1);
          }}
        />
      </div>
      <div className={styles.table}>
        <Table
          bordered
          columns={columns()}
          dataSource={dataSource}
          scroll={{ x: 'max-content', y: '60vh' }}
          rowSelection={rowSelection}
          rowKey={(record) => {
            if (!record) return 0;
            return record?.omsRecycleOrder?.id || 0;
          }}
          loading={loading}
          pagination={{
            total,
            pageSize,
            current: pageNum,
            showTotal: (total) => `${i18n.t(LOCALS.total)} ${total}`,
            showSizeChanger: true,
            onChange: (page, pageSize) => {
              commitPendingProductId();
              setPageSize(pageSize);
              setPageNum(page);
            },
          }}
        />
      </div>

      {/* -------下面是Modal弹窗-------- */}

      {/* 提交初步估值 */}
      {valuationModalElement()}

      {/* 上传shiplable */}
      {shiplableModalElement()}

      {/* 确认收货 */}
      {goodsReceivedModalElement()}

      {/* 最终报价 */}
      {finalValuationModalElement()}

      {/* 上传物流凭证 */}
      {logisticsDocumentModalElement()}

      {/* 修改报价 */}
      {updateFinalValuationModalElement()}

      {/* 确认结算 */}
      {confirmSettlementModalElement()}

      {/* 确认退货 */}
      {confirmReturnModalElement()}

      {/* 打款信息 */}
      {createPaymentVoucherModalElement()}

      {/* 意向订单-选择发货 */}
      {deliverGoodsModalElement()}

      {/* 意向订单-同意最终报价 */}
      {customerAgreeValuationModalElement()}

      {/* 意向订单-付款信息 */}
      {payInfoModalElement()}

      {/* 批量录入打款 */}
      <BatchPaymentVoucherModal
        open={batchPaymentVoucherModalOpen}
        onClose={() => setBatchPaymentVoucherModalOpen(false)}
        selectedOrderIds={selectedRowKeys}
        selectedOrders={selectedOrders}
        onSuccess={() => {
          getList();
          setSelectedRowKeys([]);
        }}
      />

      {/* 操作备注弹窗 */}
      <Modal
        open={remarkModalOpen}
        title={<Trans i18nKey={LOCALS.operation_remark} />}
        onCancel={() => {
          setRemarkModalOpen(false);
          remarkForm.resetFields();
        }}
        onOk={onRemarkOk}
      >
        <Form form={remarkForm}>
          <Form.Item
            name="note"
            label={<Trans i18nKey={LOCALS.remark} />}
            rules={[{ required: true }]}
          >
            <Input.TextArea placeholder={i18n.t(LOCALS.please_enter) || ''} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={signedContractModalOpen}
        title={i18n.t(LOCALS.signed_contract_generate_pdf)}
        width={960}
        onCancel={() => {
          if (signedContractSubmitting) return;
          setSignedContractModalOpen(false);
          signatureRef.current?.clear();
        }}
        footer={
          <>
            <Button
              onClick={() => signatureRef.current?.clear()}
              disabled={signedContractSubmitting}
            >
              {i18n.t(LOCALS.signed_contract_clear_signature)}
            </Button>
            <Button
              onClick={() => {
                if (signedContractSubmitting) return;
                setSignedContractModalOpen(false);
                signatureRef.current?.clear();
              }}
              disabled={signedContractSubmitting}
            >
              {i18n.t(LOCALS.cancel)}
            </Button>
            <Button
              type="primary"
              loading={signedContractSubmitting}
              onClick={handleSignedContractSubmit}
            >
              {i18n.t(LOCALS.signed_contract_preview_and_send)}
            </Button>
          </>
        }
      >
        <div className="mb-4 text-gray-600">
          {i18n.t(LOCALS.signed_contract_merge_hint_selected, {
            count: selectedRowKeys.length,
          })}
        </div>
        <div className="mb-2 font-medium">
          {i18n.t(LOCALS.signed_contract_customer_signature)}
        </div>
        <div className="border border-gray-300 rounded bg-white overflow-hidden">
          <SignatureCanvas
            ref={signatureRef}
            penColor="black"
            backgroundColor="white"
            canvasProps={{
              width: 900,
              height: 260,
              className: 'block w-full h-[260px]',
            }}
          />
        </div>
        <div className="mt-2 text-gray-500">
          {i18n.t(LOCALS.signed_contract_signature_hint)}
        </div>
      </Modal>
    </div>
  );
};

export default RecyclingConsignmentList;
